import { create } from 'zustand';

/**
 * Open/closed state for the Companion dock — the persistent, platform-wide AI
 * tutor surface mounted in Layout (see components/Companion/CompanionDock.tsx
 * and docs/ide-ux-spec.md).
 *
 * The dock is a singleton living in the Layout shell, so it never unmounts on
 * route changes and could hold this state locally. It lives in a store instead
 * so any surface can summon the Companion — e.g. a future "ask the Companion
 * about this error" affordance on the test panel, or the struggle-detector
 * opening it proactively — without prop-drilling through the route tree.
 *
 * Kept separate from ideStore (which churns on every keystroke) so subscribing
 * to dock state doesn't ride the editor's re-render firehose, mirroring the
 * rationale in saveStatusStore.ts.
 */

/**
 * Context about the lab/assessment the student currently has open, so the
 * platform-wide Companion can tutor with awareness of the active task. The IDE
 * page publishes this on lab load and clears it on unmount; CompanionChat reads
 * it and forwards it to the teaching agent. Mirrors the shape the in-lab
 * TeachingSidebar used to pass directly (now consolidated into the dock).
 */
export interface CompanionLabContext {
  labId: string;
  labTitle: string;
  instructions: string;
  objectives: string[];
}

export interface TranscriptSegment {
  start: number;
  end: number;
  text: string;
}

/**
 * The lecture transcript, lifted out of the video player so it can live as a
 * tab in the Companion dock (instead of a third column beside the video). The
 * LecturePlayer publishes this on transcript load and clears it on unmount; it
 * also keeps `seekTo` (which controls the player's <video>) so the transcript
 * tab can click-to-seek, and pushes the active segment index on every change so
 * the tab can highlight + auto-scroll in sync with playback.
 */
export interface CompanionLectureContext {
  videoExternalId: string;
  title?: string;
  segments: TranscriptSegment[];
  seekTo: (sec: number) => void;
}

/**
 * MV5 Sprint 2.B — Recap suggestion surfaced by the AI tutor. The api-proxy
 * chat pipeline (and later the teaching-agent) emits `recap_available` when
 * the model believes a re-explain-that segment exists; the CompanionChat
 * writes it here and the CompanionDock renders the `<RecapPlayer state="idle">`
 * chip. The lectureId is the `videos.id` UUID (not the external_id) — matched
 * against migration 027's lecture_chapters.lecture_id.
 */
export interface CompanionRecapSuggestion {
  lectureId: string;
  lectureTitle?: string;
  suggestedSegmentIds?: string[];
}

interface CompanionState {
  isOpen: boolean;
  open: () => void;
  close: () => void;
  toggle: () => void;

  labContext: CompanionLabContext | null;
  setLabContext: (ctx: CompanionLabContext | null) => void;

  lectureContext: CompanionLectureContext | null;
  setLectureContext: (ctx: CompanionLectureContext | null) => void;
  // Active transcript segment index (-1 = none). Updated from the player's
  // timeupdate, only when it changes, so the transcript tab re-renders at most
  // once per segment boundary — not on the full timeupdate firehose.
  lectureActiveSeg: number;
  setLectureActiveSeg: (i: number) => void;

  recapSuggestion: CompanionRecapSuggestion | null;
  setRecapSuggestion: (s: CompanionRecapSuggestion | null) => void;
}

export const useCompanionStore = create<CompanionState>((set) => ({
  // Ako is docked open by default — a persistent tutor presence, not a
  // click-to-open launcher. The student can still collapse it. (Mounting the
  // chat does not create a teaching session; that only happens on first
  // message, so default-open has no backend cost.)
  isOpen: true,
  open: () => set({ isOpen: true }),
  close: () => set({ isOpen: false }),
  toggle: () => set((s) => ({ isOpen: !s.isOpen })),

  labContext: null,
  setLabContext: (labContext) => set({ labContext }),

  lectureContext: null,
  setLectureContext: (lectureContext) =>
    set({ lectureContext, lectureActiveSeg: -1, recapSuggestion: null }),
  lectureActiveSeg: -1,
  setLectureActiveSeg: (lectureActiveSeg) => set({ lectureActiveSeg }),

  recapSuggestion: null,
  setRecapSuggestion: (recapSuggestion) => set({ recapSuggestion }),
}));
